import React, { useCallback, useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  Save,
  CheckCircle
} from "lucide-react";

import {
  getStudentsForMarksEntry as getStudentsOwner,
  saveMarksEntry as saveOwner,
  submitMarksEntry as submitOwner
} from "../../api/markschedule/marksApi";
import {
  getStudentsForMarksEntry as getStudentsPortal,
  saveMarksEntry as savePortal,
  submitMarksEntry as submitPortal
} from "../../api/markschedule/marksPortalApi";
import MarksEntryTable from "../../components/markschedule/MarksEntryTable";
import AssignmentStatusBadge from "../../components/markschedule/AssignmentStatusBadge";
import {
  buildMarksSavePayload,
  canEditMarks,
  pickList,
  toId,
  formatDate
} from "../../utils/markschedule/marksHelpers";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

/**
 * Teacher entry + admin view of submitted marks.
 * isPortal=false uses owner /marks APIs (admin can view any assignment).
 */
const MarksEntryPage = ({ isPortal = true }) => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [rows, setRows] = useState([]);
  const [maxMarks, setMaxMarks] = useState(0);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const backPath = isPortal
    ? "/staff-panel/marks/my-assignments"
    : "/marks/assignments";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = isPortal
        ? await getStudentsPortal(assignmentId)
        : await getStudentsOwner(assignmentId);
      setAssignment(res.assignment || null);
      setMaxMarks(res.maxMarks || res.assignment?.maxMarks || 0);
      setLocked(Boolean(res.locked));
      const list = pickList(res, ["rows", "data"]);
      setRows(
        list.map((r) => ({
          ...r,
          marksObtained: r.marksObtained,
          isAbsent: Boolean(r.isAbsent),
        }))
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load entry");
    } finally {
      setLoading(false);
    }
  }, [assignmentId, isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSubmitting(true);
      const payload = buildMarksSavePayload(assignmentId, rows);
      if (isPortal) await savePortal(payload);
      else await saveOwner(payload);
      toast.success("Draft saved");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (
      !window.confirm(
        "Submit and lock marks? You will not be able to edit after this."
      )
    )
      return;
    try {
      setSubmitting(true);
      const payload = buildMarksSavePayload(assignmentId, rows);
      if (isPortal) {
        await savePortal(payload);
        await submitPortal(assignmentId);
      } else {
        await saveOwner(payload);
        await submitOwner(assignmentId);
      }
      toast.success("Submitted and locked");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <Skeleton height={24} width="100px" />
        <Skeleton height={80} width="100%" />
        <Skeleton height={300} width="100%" />
      </div>
    );
  }

  const editable = canEditMarks(assignment) && !locked;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => navigate(backPath)}
        className="inline-flex items-center gap-1 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header Info Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {assignment?.examName || assignment?.examId?.name || "Marks Entry"}
            </CardTitle>
            <AssignmentStatusBadge status={assignment?.status} />
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            {assignment?.subjectId?.name || "Subject"} ·{" "}
            {assignment?.classId?.className || "Class"} ·{" "}
            {assignment?.sectionId?.sectionName || "Section"} · Max {maxMarks}
            {assignment?.examDate ? ` · ${formatDate(assignment.examDate)}` : ""}
            {assignment?.assignedStaffId?.name
              ? ` · ${assignment.assignedStaffId.name}`
              : ""}
          </p>
        </CardHeader>
      </Card>

      {/* Alerts */}
      
      

      {/* Marks Table Grid */}
      <MarksEntryTable
        rows={rows}
        maxMarks={maxMarks}
        locked={!editable}
        onChange={setRows}
      />

      {/* Action Footer */}
      {editable ? (
        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="secondary"
            isLoading={submitting}
            onClick={handleSave}
            leftIcon={<Save className="h-4 w-4" />}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={submitting}
            onClick={handleSubmit}
            leftIcon={<CheckCircle className="h-4 w-4" />}
          >
            Submit &amp; Lock
          </Button>
        </div>
      ) : (
        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
          Read-only — assignment is {assignment?.status || "locked"}. Admin can reopen from Submitted marks list.
        </p>
      )}
    </div>
  );
};

export default MarksEntryPage;